import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import useSiteSettings from "@/hooks/useSiteSettings";

const KEY = "jw_popup_v1";

/**
 * Discount / newsletter popup. All copy is editable from Settings admin —
 * we fall back to i18n strings when a field is empty so old accounts still
 * see something meaningful.
 */
export default function DiscountPopup() {
    const { t, lang } = useI18n();
    const { settings } = useSiteSettings();
    const cfg = settings.discount_popup || {};
    const es = lang === "es";
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (cfg.enabled === false) return;
        if (localStorage.getItem(KEY) === "shown") return;
        const delay = (Number(cfg.delay_seconds) > 0 ? Number(cfg.delay_seconds) : 8) * 1000;
        const id = setTimeout(() => setOpen(true), delay);
        return () => clearTimeout(id);
    }, [cfg.enabled, cfg.delay_seconds]);

    const close = () => {
        localStorage.setItem(KEY, "shown");
        setOpen(false);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!email.includes("@")) {
            toast.error("Enter a valid email");
            return;
        }
        setBusy(true);
        try {
            await api.post("/subscribers", { email, source: "discount_popup" });
            toast.success(t("popup.ok"));
            close();
        } catch {
            toast.error("Try again later");
        } finally { setBusy(false); }
    };

    const kicker = (es ? cfg.kicker_es : cfg.kicker_en) || (es ? "Tiempo limitado" : "Limited time");
    const title  = (es ? cfg.title_es : cfg.title_en)   || t("popup.title");
    const body   = (es ? cfg.body_es : cfg.body_en)     || t("popup.body");
    const cta    = (es ? cfg.cta_es : cfg.cta_en)       || t("popup.cta");

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4"
                    data-testid="discount-popup"
                    onClick={close}
                >
                    <motion.div
                        onClick={(e) => e.stopPropagation()}
                        initial={{ scale: 0.92, y: 20 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0.92, y: 20 }}
                        className="bg-white rounded-3xl max-w-md w-full overflow-hidden relative"
                    >
                        <button
                            onClick={close}
                            data-testid="popup-close"
                            className="absolute top-4 right-4 z-10 h-9 w-9 grid place-items-center rounded-full bg-white/80 hover:bg-white border border-border"
                        >
                            <X size={16} weight="bold" />
                        </button>
                        <div className="bg-primary text-primary-foreground p-8 relative overflow-hidden">
                            <Sparkle size={120} weight="fill" className="absolute -top-4 -right-4 opacity-15" />
                            <p className="text-xs font-bold uppercase tracking-[0.25em] text-secondary">
                                {kicker}
                            </p>
                            <h3 className="font-display font-black text-3xl mt-2 leading-tight">
                                {title}
                            </h3>
                        </div>
                        <form onSubmit={submit} className="p-8 space-y-4">
                            <p className="text-sm text-muted-foreground">{body}</p>
                            <Input
                                type="email"
                                placeholder={t("popup.placeholder")}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="popup-email"
                                required
                            />
                            <Button
                                type="submit"
                                disabled={busy}
                                data-testid="popup-submit"
                                className="w-full py-6 rounded-full bg-foreground hover:bg-foreground/90 text-background font-bold uppercase tracking-widest"
                            >
                                {busy ? "..." : cta}
                            </Button>
                        </form>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
